import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  InlineStack,
  BlockStack,
  Text,
  Pagination,
  SkeletonBodyText,
  EmptyState,
  Box,
} from '@shopify/polaris';
import { warehouseApi } from '@/services';
import type { DispatchQueueItem } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

export default function WarehouseQueue() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['warehouse-queue', page, status, searchQuery],
    queryFn: () =>
      warehouseApi.getDispatchQueue({
        page,
        limit,
        status: status[0],
      }),
  });

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setSearchQuery('');
  }, []);

  const getFulfillmentBadge = (item: DispatchQueueItem) => {
    switch (item.fulfillmentStatus) {
      case 'ready':
        return <Badge tone="success">Ready to Ship</Badge>;
      case 'partial':
        return <Badge tone="warning">{`Partial (${item.assignedSerials}/${item.requiredSerials})`}</Badge>;
      default:
        return <Badge>{`Needs Serials (${item.assignedSerials}/${item.requiredSerials})`}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { tone: 'success' | 'warning' | 'info' | 'critical' | undefined; label: string }> = {
      pending: { tone: undefined, label: 'Pending' },
      processing: { tone: 'info', label: 'Processing' },
      ready_to_ship: { tone: 'success', label: 'Ready to Ship' },
    };
    const config = statusMap[status] || { tone: undefined, label: status };
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  const rows = (data?.data || []).map((item) => [
    <BlockStack key={item.id} gap="100">
      <Button variant="plain" onClick={() => navigate(`/warehouse/dispatch/${item.id}`)}>
        {orderNumber(item)}
      </Button>
      {item.duplicateBlocked && (
        <>
          <Badge tone="critical">Possible duplicate — held</Badge>
          <Text as="span" tone="subdued" variant="bodySm">
            {`Possible duplicate of order ${item.duplicateOfOrderNumber || 'on file'} — an admin must clear or cancel it before dispatch`}
          </Text>
        </>
      )}
    </BlockStack>,
    item.clinic?.name || item.patientName || 'N/A',
    item.rentalStartDate ? new Date(item.rentalStartDate).toLocaleDateString() : 'N/A',
    String(item.items.length),
    getFulfillmentBadge(item),
    getStatusBadge(item.rentalStatus),
    // Held orders open to the review banner but cannot be processed from here.
    <Button key={`action-${item.id}`} disabled={item.duplicateBlocked} onClick={() => navigate(`/warehouse/dispatch/${item.id}`)}>
      Process
    </Button>,
  ]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Pending', value: 'pending' },
            { label: 'Processing', value: 'processing' },
            { label: 'Ready to Ship', value: 'ready_to_ship' },
          ]}
          selected={status}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = status.length > 0
    ? [
        {
          key: 'status',
          label: `Status: ${status.join(', ')}`,
          onRemove: () => setStatus([]),
        },
      ]
    : [];

  if (error) {
    return (
      <Page title="Dispatch Queue">
        <Card>
          <EmptyState
            heading="Error loading dispatch queue"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Dispatch Queue"
      primaryAction={{
        content: 'Scan to Dispatch',
        onAction: () => navigate('/warehouse/dispatch'),
      }}
    >
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search orders..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleSearchChange}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={handleClearFilters}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No orders pending dispatch"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>All orders have been processed.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text', 'text']}
              headings={['Order', 'Customer', 'Start Date', 'Items', 'Fulfillment', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} orders
          </Text>
        </Box>
      )}
    </Page>
  );
}
